import React, { memo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trash2, FolderTree, Plus } from 'lucide-react';
import type { ConditionCategoryWithCount } from '../hooks/useConditionManagement';

interface ManageCategoriesModalProps {
  isOpen: boolean;
  onClose: () => void;
  categories: ConditionCategoryWithCount[];
  onDelete: (id: string) => void;
  onCreate: (name: string) => Promise<void>;
  isDeleting?: boolean;
  isCreating?: boolean;
}

export const ManageCategoriesModal: React.FC<ManageCategoriesModalProps> = memo(({
  isOpen,
  onClose,
  categories,
  onDelete,
  onCreate,
  isDeleting = false,
  isCreating = false
}) => {
  const [showNewInput, setShowNewInput] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');

  if (!isOpen) return null;

  const handleCreate = async () => {
    if (!newCategoryName.trim()) {
      return;
    }
    await onCreate(newCategoryName.trim());
    setNewCategoryName('');
    setShowNewInput(false);
  };

  const handleCancel = () => {
    setNewCategoryName('');
    setShowNewInput(false);
  };

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4"
      style={{
        zIndex: 99999,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="rounded-2xl shadow-2xl max-w-2xl w-full max-h-[95vh] overflow-y-auto border relative"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-xl)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div 
          className="sticky top-0 rounded-t-2xl p-6 pb-4 z-[70]"
          style={{
            backgroundColor: 'var(--color-bg-secondary)',
            borderBottom: '1px solid var(--color-border)'
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div 
                className="p-2 rounded-lg"
                style={{
                  backgroundColor: 'var(--color-bg-tertiary)'
                }}
              >
                <FolderTree className="w-6 h-6" style={{ color: 'var(--color-accent)' }} />
              </div>
              <div>
                <h3 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                  Manage Categories
                </h3>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  View and manage condition categories
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowNewInput(true)}
                className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors"
                style={{
                  backgroundColor: 'var(--color-accent)',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.opacity = '0.9';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = '1';
                }}
              >
                <Plus className="w-4 h-4" />
                <span>New</span>
              </button>
              <button
                onClick={onClose}
                className="p-2 rounded-lg transition-colors duration-200"
                style={{
                  color: 'var(--color-text-muted)'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                  e.currentTarget.style.color = 'var(--color-text-secondary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {showNewInput && (
            <div className="mb-4 p-4 rounded-lg border" style={{
              backgroundColor: 'var(--color-bg-secondary)',
              borderColor: 'var(--color-border)'
            }}>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={newCategoryName}
                  onChange={(e) => setNewCategoryName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleCreate();
                    } else if (e.key === 'Escape') {
                      handleCancel();
                    }
                  }}
                  className="flex-1 px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  style={{
                    backgroundColor: 'var(--color-bg-primary)',
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-primary)'
                  }}
                  placeholder="Enter new category name..."
                  autoFocus
                />
                <button
                  onClick={handleCreate}
                  disabled={!newCategoryName.trim() || isCreating}
                  className="px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                  style={{
                    backgroundColor: 'var(--color-accent)',
                    color: 'white'
                  }}
                >
                  {isCreating ? 'Creating...' : 'Create'}
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 border rounded-lg transition-colors"
                  style={{
                    borderColor: 'var(--color-border)',
                    color: 'var(--color-text-secondary)'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
          {categories.length === 0 && !showNewInput ? (
            <div className="text-center py-8" style={{ color: 'var(--color-text-secondary)' }}>
              <p>No categories found.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between p-4 rounded-lg border"
                  style={{
                    backgroundColor: 'var(--color-bg-secondary)',
                    borderColor: 'var(--color-border)'
                  }}
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {category.name}
                      </span>
                      <span 
                        className="text-sm px-2 py-1 rounded"
                        style={{
                          backgroundColor: 'var(--color-bg-tertiary)',
                          color: 'var(--color-text-secondary)'
                        }}
                      >
                        {category.conditionCount} {category.conditionCount === 1 ? 'condition' : 'conditions'}
                      </span>
                    </div>
                  </div>
                  {category.conditionCount === 0 && (
                    <button
                      onClick={() => onDelete(category.id)}
                      disabled={isDeleting}
                      className="p-2 rounded-lg transition-colors disabled:opacity-50"
                      style={{
                        color: 'var(--color-error)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isDeleting) {
                          e.currentTarget.style.backgroundColor = 'var(--color-bg-tertiary)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      title="Delete category"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                  {category.conditionCount > 0 && (
                    <span 
                      className="text-sm px-3 py-1 rounded"
                      style={{
                        color: 'var(--color-text-muted)',
                        fontStyle: 'italic'
                      }}
                    >
                      Cannot delete
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Render modal in a portal to document.body to ensure it's above everything
  return typeof document !== 'undefined' 
    ? createPortal(modalContent, document.body)
    : null;
});

ManageCategoriesModal.displayName = 'ManageCategoriesModal';

